import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'

import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'
import { OrderStatus } from '@/core/models/order.model'
import { ConsultationStatus, ConsultationType } from '@/core/models/consultation.model'
import { useAcceptMeetingLinkOrderMutation, useRefuseMeetingLinkOrderMutation, useUpdateMeetingLinkOrderMutation } from '@/core/services/order'
import { useCreateConsultationMutation } from '@/core/services/consultation'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'

type PendingOrdersQueueProps = {
  orders: MeetingLinkOrder[] | undefined
  onOrderClick?: (orderId: string) => void
}

export default function PendingOrdersQueue(props: PendingOrdersQueueProps) {
  const { orders, onOrderClick } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const authUser = useAppSelector(selectAuthUser)

  const [acceptOrder] = useAcceptMeetingLinkOrderMutation()
  const [refuseOrder] = useRefuseMeetingLinkOrderMutation()
  const [updateOrder] = useUpdateMeetingLinkOrderMutation()
  const [createConsultation] = useCreateConsultationMutation()

  const pendingOrders = orders?.filter((order) => order.status === OrderStatus.Pending) ?? []

  if (pendingOrders.length === 0) return null

  const handleAccept = async (orderId: string) => {
    try {
      await acceptOrder(orderId).unwrap()
      enqueueSnackbar(t('page.appointments.notification.accepted'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.acceptError')), { variant: 'error' })
    }
  }

  const handleRefuse = async (orderId: string) => {
    try {
      await refuseOrder(orderId).unwrap()
      enqueueSnackbar(t('page.appointments.notification.refused'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.refuseError')), { variant: 'error' })
    }
  }

  return (
    <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {t('page.appointments.pendingQueue.title')} ({pendingOrders.length})
      </Typography>
      <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1 }}>
        {pendingOrders.map((order) => {
          const patientName = order.createdBy
            ? `${order.createdBy.userInfo?.name} ${order.createdBy.userInfo?.surname}`
            : '-'

          return (
            <Card
              key={order._id}
              sx={{ minWidth: 260, cursor: 'pointer', flexShrink: 0 }}
              onClick={() => onOrderClick?.(order._id)}
            >
              <CardContent sx={{ p: '12px !important' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {patientName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {dayjs(order.startDate).format('DD/MM/YYYY HH:mm')}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                  <Chip label={order.speciality} size="small" variant="outlined" />
                  <Chip label={order.meetingPlace?.description} size="small" variant="outlined" />
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => handleAccept(order._id)}
                  >
                    {t('page.appointments.action.accept')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => handleRefuse(order._id)}
                  >
                    {t('page.appointments.action.refuse')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          )
        })}
      </Stack>
    </Box>
  )
}
